/**
 * DutyManagerPage — لوحة مدير المناوبة
 *
 * Tabs:
 *  1. المناوبة الحالية  — بدء/إنهاء مناوبة، إضافة موظفين، شكاوى، طلبات اتصال
 *  2. التقرير           — إعداد وحفظ تقرير المناوبة (يُفعَّل بعد انتهاء وقت المناوبة)
 *  3. الأحداث           — أحداث القسم (نفس SupervisorPage مع صلاحيات DutyManager)
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@munawb/hooks';
import { useIncidents, useIncidentAction } from '@munawb/hooks';
import { useNotifications, useMarkNotificationsRead } from '@munawb/hooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@munawb/supabase';
import type { IncidentRow, ProfileWithDept } from '@munawb/types';
import { RISK_LEVEL_COLORS, formatSlaTime, formatDate } from '../lib/utils';

/* ── Types ─────────────────────────────────────────────────────── */

type TabId = 'shift' | 'report' | 'incidents';

interface ActiveShift {
  id:         string;
  shift_id:   string;
  shift_date: string;
  period:     string;
  start_time: string;
  end_time:   string;
  status:     string;
  report_saved: boolean;
}

interface StaffMember {
  id?:        string;
  role_title: string;
  staff_name: string;
  check_in:   string;
}

interface Complaint {
  id:         string;
  comp_name:  string;
  comp_type:  string;
  details:    string;
  status:     string;
  created_at: string;
}

interface CallGroup {
  id:   string;
  name: string;
}

interface CallRequest {
  id:           string;
  requester_id: string;
  group_id:     string | null;
  call_type:    string | null;
  location:     string | null;
  description:  string | null;
  status:       'pending' | 'acknowledged' | 'resolved';
  created_at:   string;
  call_groups:  { name: string } | null;
}

/* ── Helpers ────────────────────────────────────────────────────── */

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function nowTime(): string {
  return new Date().toTimeString().slice(0, 5);
}

function isShiftEnded(endTime: string, shiftDate: string): boolean {
  const now   = new Date();
  const [h, m] = endTime.split(':').map(Number);
  const end   = new Date(shiftDate);
  end.setHours(h, m, 0, 0);
  // Night shifts may end past midnight
  if (h < 8) end.setDate(end.getDate() + 1);
  return now > end;
}

const PERIODS = ['صباحية', 'مسائية', 'ليلية'] as const;
const DEFAULT_STAFF_ROLES = [
  'طبيب مناوب', 'ممرض مناوب', 'مشرف قسم', 'صيدلاني', 'فني مختبر',
  'فني أشعة', 'سكرتير طبي', 'حارس أمن', 'عامل نظافة',
];

/* ═══════════════════════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════════════════════════ */

export default function DutyManagerPage() {
  const { profile } = useAuthStore();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('shift');

  /* ── Active Shift ─────────────────────────────────────────── */
  const { data: activeShift, isLoading: shiftLoading } = useQuery<ActiveShift | null>({
    queryKey: ['duty-shift-active', profile?.id],
    queryFn:  async () => {
      if (!profile?.id) return null;
      const { data } = await supabase
        .from('duty_shifts')
        .select('*')
        .eq('manager_id', profile.id)
        .eq('shift_date', todayISO())
        .in('status', ['active', 'ended', 'report_pending'])
        .maybeSingle();
      return (data as ActiveShift | null);
    },
    refetchInterval: 60_000,
  });

  const shiftEnded = activeShift
    ? isShiftEnded(activeShift.end_time, activeShift.shift_date)
    : false;

  const reportPending = activeShift && shiftEnded && !activeShift.report_saved;

  /* ── Notifications ────────────────────────────────────────── */
  const { data: notifications = [] } = useNotifications(profile?.id ?? '', null, true);
  const { mutate: markRead } = useMarkNotificationsRead(profile?.id ?? '');
  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleSignOut = async () => { await supabase.auth.signOut(); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950">

      {/* Header */}
      <header className="glass-panel border-b border-slate-200/80 dark:border-slate-700/50 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/munawb-logo.png" alt="" className="w-8 h-8 rounded-xl" />
            <div>
              <span className="font-black text-slate-800 dark:text-white text-sm">مُناوِب</span>
              <span className="mr-2 text-xs text-slate-500 dark:text-slate-400">— مدير المناوبة</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="relative p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              onClick={() => unreadCount > 0 && markRead(notifications.filter(n => !n.is_read).map(n => n.id))}
            >
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[10px] font-black flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <span className="text-xs font-bold text-slate-600 dark:text-slate-400">{profile?.name}</span>
            <button onClick={handleSignOut} className="btn-secondary text-xs px-3 py-1.5">خروج</button>
          </div>
        </div>
      </header>

      {/* Report pending banner */}
      {reportPending && (
        <div className="bg-amber-500 text-white text-center py-2.5 px-4 text-sm font-black">
          ⚠️ انتهت فترة مناوبتك — يرجى إعداد التقرير وحفظه قبل تسجيل مناوبة جديدة
          <button
            onClick={() => setActiveTab('report')}
            className="mr-3 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-black transition-colors"
          >
            إعداد التقرير
          </button>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 py-6">

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {([
            { id: 'shift',     label: 'المناوبة' },
            { id: 'report',    label: 'التقرير', badge: reportPending },
            { id: 'incidents', label: 'الأحداث' },
          ] as { id: TabId; label: string; badge?: boolean }[]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative px-5 py-2.5 rounded-xl font-black text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50'
              }`}
            >
              {tab.label}
              {tab.badge && (
                <span className="absolute -top-1.5 -left-1.5 w-4 h-4 bg-amber-500 rounded-full" />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'shift' && (
          <ShiftTab
            profile={profile}
            activeShift={activeShift ?? null}
            shiftEnded={shiftEnded}
            reportPending={!!reportPending}
            isLoading={shiftLoading}
            onShiftChange={() => qc.invalidateQueries({ queryKey: ['duty-shift-active'] })}
          />
        )}
        {activeTab === 'report' && (
          <ReportTab
            activeShift={activeShift ?? null}
            reportPending={!!reportPending}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ['duty-shift-active'] });
              setActiveTab('shift');
            }}
          />
        )}
        {activeTab === 'incidents' && (
          <IncidentsTab profile={profile} />
        )}

      </main>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ShiftTab
══════════════════════════════════════════════════════════════════ */

function ShiftTab({
  profile, activeShift, shiftEnded, reportPending, isLoading, onShiftChange,
}: {
  profile: ProfileWithDept | null;
  activeShift: ActiveShift | null;
  shiftEnded: boolean;
  reportPending: boolean;
  isLoading: boolean;
  onShiftChange: () => void;
}) {
  const qc = useQueryClient();

  // New shift form state
  const [period,    setPeriod]    = useState<typeof PERIODS[number]>('صباحية');
  const [startTime, setStartTime] = useState(nowTime());
  const [endTime,   setEndTime]   = useState('');
  const [staffList, setStaffList] = useState<StaffMember[]>([
    { role_title: 'طبيب مناوب', staff_name: '', check_in: '' },
  ]);
  const [submitError, setSubmitError] = useState('');

  // Complaints for active shift
  const { data: complaints = [] } = useQuery<Complaint[]>({
    queryKey: ['shift-complaints', activeShift?.id],
    queryFn:  async () => {
      if (!activeShift?.id) return [];
      const { data } = await supabase
        .from('duty_complaints')
        .select('id, comp_name, comp_type, details, status, created_at')
        .eq('shift_id', activeShift.id)
        .order('created_at', { ascending: false });
      return (data ?? []) as Complaint[];
    },
    enabled: !!activeShift?.id,
  });

  // Create shift
  const { mutateAsync: createShift, isPending: creating } = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/duty-shifts-create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token ?? ''}`,
          },
          body: JSON.stringify({
            shift_date: todayISO(),
            period,
            start_time: startTime,
            end_time:   endTime,
            staff: staffList.filter(s => s.staff_name.trim()),
          }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? 'فشل في إنشاء المناوبة');
      return json;
    },
    onSuccess: () => { onShiftChange(); setSubmitError(''); },
    onError:   (err: Error) => setSubmitError(err.message),
  });

  const addStaffRow = () =>
    setStaffList(prev => [...prev, { role_title: '', staff_name: '', check_in: '' }]);

  const removeStaffRow = (i: number) =>
    setStaffList(prev => prev.filter((_, idx) => idx !== i));

  const updateStaff = (i: number, field: keyof StaffMember, value: string) =>
    setStaffList(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  if (isLoading) return (
    <div className="glass-panel rounded-2xl p-10 text-center">
      <span className="spinner border-blue-200 border-t-blue-600" />
    </div>
  );

  /* Active shift view */
  if (activeShift) {
    return (
      <div className="space-y-4">

        {/* Shift status card */}
        <div className={`glass-panel rounded-2xl p-6 border-r-4 ${
          shiftEnded ? 'border-amber-500' : 'border-green-500'
        }`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`w-2.5 h-2.5 rounded-full ${shiftEnded ? 'bg-amber-500' : 'bg-green-500 animate-pulse'}`} />
                <span className="text-xs font-black text-slate-500">
                  {shiftEnded ? 'انتهت المناوبة' : 'مناوبة نشطة'}
                </span>
              </div>
              <h2 className="text-xl font-black text-slate-800 dark:text-white">
                {activeShift.shift_id}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {activeShift.period} — {activeShift.shift_date}
              </p>
            </div>
            <div className="text-left">
              <p className="text-xs text-slate-400 font-bold">وقت المناوبة</p>
              <p className="text-base font-black text-slate-800 dark:text-white" dir="ltr">
                {activeShift.start_time} — {activeShift.end_time}
              </p>
            </div>
          </div>
        </div>

        {/* Staff list */}
        <ShiftStaffSection shiftId={activeShift.id} />

        {/* Complaints */}
        <ComplaintsSection shiftId={activeShift.id} complaints={complaints} />

        {/* Call Requests */}
        <CallRequestsSection managerId={profile?.id ?? ''} />

      </div>
    );
  }

  /* No active shift — registration form */
  return (
    <div className="glass-panel rounded-2xl p-6">
      <h2 className="text-base font-black text-slate-800 dark:text-white mb-5">تسجيل مناوبة جديدة</h2>

      {reportPending && (
        <div className="mb-5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <p className="text-amber-700 dark:text-amber-400 text-sm font-bold">
            لا يمكن تسجيل مناوبة جديدة — يرجى إنشاء تقرير المناوبة السابقة أولاً
          </p>
        </div>
      )}

      <div className="space-y-4" style={{ pointerEvents: reportPending ? 'none' : 'auto', opacity: reportPending ? 0.5 : 1 }}>

        {/* Period + Times */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">الفترة</label>
            <select className="input-field" value={period} onChange={e => setPeriod(e.target.value as typeof PERIODS[number])}>
              {PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">وقت البداية</label>
            <input type="time" className="input-field" dir="ltr" value={startTime} onChange={e => setStartTime(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">وقت النهاية</label>
            <input type="time" className="input-field" dir="ltr" value={endTime} onChange={e => setEndTime(e.target.value)} />
          </div>
        </div>

        {/* Staff table */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-black text-slate-600 dark:text-slate-400">كادر المناوبة</label>
            <button onClick={addStaffRow} className="text-xs font-black text-blue-600 hover:text-blue-700">
              + إضافة صف
            </button>
          </div>
          <div className="space-y-2">
            {staffList.map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_100px_32px] gap-2 items-center">
                <select
                  className="input-field text-xs"
                  value={s.role_title}
                  onChange={e => updateStaff(i, 'role_title', e.target.value)}
                >
                  <option value="">المسمى</option>
                  {DEFAULT_STAFF_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <input
                  type="text"
                  className="input-field text-xs"
                  placeholder="اسم الموظف"
                  value={s.staff_name}
                  onChange={e => updateStaff(i, 'staff_name', e.target.value)}
                />
                <input
                  type="time"
                  className="input-field text-xs"
                  dir="ltr"
                  value={s.check_in}
                  onChange={e => updateStaff(i, 'check_in', e.target.value)}
                />
                <button
                  onClick={() => removeStaffRow(i)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {submitError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 text-center">
            <p className="text-red-600 dark:text-red-400 text-sm font-bold">{submitError}</p>
          </div>
        )}

        <button
          onClick={() => !reportPending && createShift()}
          disabled={creating || reportPending || !endTime}
          className="btn-primary w-full py-3"
        >
          {creating
            ? <><span className="spinner border-white/30 border-t-white" /> جارٍ التسجيل...</>
            : 'تسجيل المناوبة'}
        </button>
      </div>
    </div>
  );
}

/* ── ShiftStaffSection ──────────────────────────────────────────── */

function ShiftStaffSection({ shiftId }: { shiftId: string }) {
  const { data: staff = [] } = useQuery<{ id: string; role_title: string; staff_name: string; check_in: string | null }[]>({
    queryKey: ['shift-staff', shiftId],
    queryFn:  async () => {
      const { data } = await supabase
        .from('duty_shift_staff')
        .select('id, role_title, staff_name, check_in')
        .eq('shift_id', shiftId)
        .order('sort_order');
      return data ?? [];
    },
  });

  if (!staff.length) return null;

  return (
    <div className="glass-panel rounded-2xl p-5">
      <h3 className="text-sm font-black text-slate-800 dark:text-white mb-3">كادر المناوبة</h3>
      <div className="space-y-2">
        {staff.map(s => (
          <div key={s.id} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-white">{s.staff_name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{s.role_title}</p>
            </div>
            {s.check_in && (
              <span className="text-xs font-bold text-green-600 dark:text-green-400" dir="ltr">
                {s.check_in}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── ComplaintsSection ──────────────────────────────────────────── */

function ComplaintsSection({ shiftId, complaints }: { shiftId: string; complaints: Complaint[] }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ comp_name: '', comp_type: 'حضورية', details: '' });

  const { mutateAsync: addComplaint, isPending } = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('duty_complaints').insert({
        shift_id:  shiftId,
        ...form,
        status:    'مفتوح',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift-complaints', shiftId] });
      setForm({ comp_name: '', comp_type: 'حضورية', details: '' });
      setShowForm(false);
    },
  });

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-black text-slate-800 dark:text-white">
          الشكاوى ({complaints.length})
        </h3>
        <button onClick={() => setShowForm(v => !v)} className="text-xs font-black text-blue-600 hover:text-blue-700">
          {showForm ? 'إلغاء' : '+ شكوى جديدة'}
        </button>
      </div>

      {showForm && (
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-black text-slate-500 mb-1">اسم المتقدم</label>
              <input type="text" className="input-field text-sm" value={form.comp_name}
                onChange={e => setForm(p => ({ ...p, comp_name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-black text-slate-500 mb-1">نوع الشكوى</label>
              <select className="input-field text-sm" value={form.comp_type}
                onChange={e => setForm(p => ({ ...p, comp_type: e.target.value }))}>
                <option value="حضورية">حضورية</option>
                <option value="خطية">خطية</option>
                <option value="937">937</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-black text-slate-500 mb-1">تفاصيل الشكوى</label>
            <textarea rows={3} className="input-field text-sm resize-none" value={form.details}
              onChange={e => setForm(p => ({ ...p, details: e.target.value }))} />
          </div>
          <button onClick={() => addComplaint()} disabled={isPending || !form.comp_name || !form.details}
            className="btn-primary text-xs w-full py-2">
            {isPending ? <span className="spinner border-white/30 border-t-white" /> : 'إضافة الشكوى'}
          </button>
        </div>
      )}

      {complaints.length === 0 ? (
        <p className="text-slate-400 text-xs font-bold text-center py-4">لا توجد شكاوى</p>
      ) : (
        <div className="space-y-2">
          {complaints.map(c => (
            <div key={c.id} className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
              <div>
                <p className="text-sm font-bold text-slate-800 dark:text-white">{c.comp_name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{c.details}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{formatDate(c.created_at)}</p>
              </div>
              <div className="shrink-0 flex flex-col items-end gap-1">
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                  {c.comp_type}
                </span>
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                  c.status === 'مغلق' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {c.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ReportTab
══════════════════════════════════════════════════════════════════ */

function ReportTab({
  activeShift, reportPending, onSaved,
}: {
  activeShift: ActiveShift | null;
  reportPending: boolean;
  onSaved: () => void;
}) {
  const [content, setContent] = useState('');
  const [summary, setSummary] = useState('');
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);

  const { mutateAsync: saveReport, isPending } = useMutation({
    mutationFn: async () => {
      if (!activeShift) throw new Error('لا توجد مناوبة نشطة');
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/duty-shifts-report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token ?? ''}`,
          },
          body: JSON.stringify({ shift_id: activeShift.id, content, summary }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? 'فشل في حفظ التقرير');
      return json;
    },
    onSuccess: () => {
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onSaved(); }, 2000);
    },
    onError: (err: Error) => setError(err.message),
  });

  if (!activeShift) {
    return (
      <div className="glass-panel rounded-2xl p-10 text-center">
        <p className="text-slate-400 font-bold text-sm">لا توجد مناوبة نشطة لإعداد تقريرها</p>
      </div>
    );
  }

  if (activeShift.report_saved) {
    return (
      <div className="glass-panel rounded-2xl p-10 text-center">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="font-black text-slate-800 dark:text-white">تم حفظ التقرير</p>
        <p className="text-sm text-slate-500 mt-1">المناوبة {activeShift.shift_id} مغلقة</p>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-2xl p-6 space-y-5">
      <div>
        <h2 className="text-base font-black text-slate-800 dark:text-white">تقرير المناوبة</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {activeShift.shift_id} — {activeShift.period} — {activeShift.shift_date}
        </p>
      </div>

      {!reportPending && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
          <p className="text-amber-700 dark:text-amber-400 text-sm font-bold">
            التقرير متاح فقط بعد انتهاء وقت المناوبة
          </p>
        </div>
      )}

      <div style={{ pointerEvents: reportPending ? 'auto' : 'none', opacity: reportPending ? 1 : 0.5 }}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              ملخص المناوبة <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="ملخص مختصر لأبرز أحداث المناوبة"
              value={summary}
              onChange={e => setSummary(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              تفاصيل التقرير
            </label>
            <textarea
              rows={8}
              className="input-field resize-none"
              placeholder="اكتب تفاصيل تقرير المناوبة هنا: الأحداث، القرارات، التوصيات..."
              value={content}
              onChange={e => setContent(e.target.value)}
            />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
              <p className="text-red-600 dark:text-red-400 text-sm font-bold text-center">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3">
              <p className="text-green-600 dark:text-green-400 text-sm font-bold text-center">
                ✓ تم حفظ التقرير وإغلاق المناوبة
              </p>
            </div>
          )}

          <button
            onClick={() => saveReport()}
            disabled={isPending || !summary.trim() || success}
            className="btn-primary w-full py-3"
          >
            {isPending
              ? <><span className="spinner border-white/30 border-t-white" /> جارٍ الحفظ...</>
              : 'حفظ التقرير وإغلاق المناوبة'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── CallRequestsSection ────────────────────────────────────────── */

function CallRequestsSection({ managerId }: { managerId: string }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    group_id:    '',
    call_type:   '',
    location:    '',
    description: '',
  });

  // Load all call_groups
  const { data: groups = [] } = useQuery<CallGroup[]>({
    queryKey: ['call-groups'],
    queryFn:  async () => {
      const { data } = await supabase
        .from('call_groups')
        .select('id, name')
        .eq('active', true)
        .order('name');
      return (data ?? []) as CallGroup[];
    },
  });

  // Load call requests visible to this manager (RLS returns own + all for DutyManager+)
  const { data: requests = [] } = useQuery<CallRequest[]>({
    queryKey: ['call-requests'],
    queryFn:  async () => {
      const { data } = await supabase
        .from('call_requests')
        .select('id, requester_id, group_id, call_type, location, description, status, created_at, call_groups(name)')
        .order('created_at', { ascending: false })
        .limit(20);
      return (data ?? []) as unknown as CallRequest[];
    },
    refetchInterval: 30_000,
  });

  const { mutateAsync: createRequest, isPending: creating } = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('call_requests').insert({
        requester_id: managerId,
        group_id:     form.group_id   || null,
        call_type:    form.call_type  || null,
        location:     form.location   || null,
        description:  form.description || null,
        status:       'pending',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['call-requests'] });
      setForm({ group_id: '', call_type: '', location: '', description: '' });
      setShowForm(false);
    },
  });

  const { mutateAsync: updateStatus } = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'acknowledged' | 'resolved' }) => {
      const { error } = await supabase
        .from('call_requests')
        .update({ status })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['call-requests'] }),
  });

  const statusLabel: Record<CallRequest['status'], string> = {
    pending:      'قيد الانتظار',
    acknowledged: 'تم الاستلام',
    resolved:     'مُغلق',
  };
  const statusColors: Record<CallRequest['status'], string> = {
    pending:      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    acknowledged: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    resolved:     'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  };

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-black text-slate-800 dark:text-white">
          طلبات الاتصال ({requests.filter(r => r.status !== 'resolved').length})
        </h3>
        <button
          onClick={() => setShowForm(v => !v)}
          className="text-xs font-black text-blue-600 hover:text-blue-700"
        >
          {showForm ? 'إلغاء' : '+ طلب اتصال جديد'}
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-black text-slate-500 mb-1">المجموعة</label>
              <select
                className="input-field text-sm"
                value={form.group_id}
                onChange={e => setForm(p => ({ ...p, group_id: e.target.value }))}
              >
                <option value="">— بدون تحديد —</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-black text-slate-500 mb-1">نوع الاتصال</label>
              <input
                type="text"
                className="input-field text-sm"
                placeholder="استشارة / إسعاف / صيانة..."
                value={form.call_type}
                onChange={e => setForm(p => ({ ...p, call_type: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-black text-slate-500 mb-1">الموقع</label>
            <input
              type="text"
              className="input-field text-sm"
              placeholder="الطابق الثالث — جناح B"
              value={form.location}
              onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-black text-slate-500 mb-1">التفاصيل</label>
            <textarea
              rows={2}
              className="input-field text-sm resize-none"
              placeholder="وصف مختصر للطلب..."
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            />
          </div>
          <button
            onClick={() => createRequest()}
            disabled={creating || (!form.call_type && !form.group_id)}
            className="btn-primary text-xs w-full py-2"
          >
            {creating
              ? <span className="spinner border-white/30 border-t-white" />
              : 'إرسال الطلب'}
          </button>
        </div>
      )}

      {/* Requests List */}
      {requests.length === 0 ? (
        <p className="text-slate-400 text-xs font-bold text-center py-4">لا توجد طلبات اتصال</p>
      ) : (
        <div className="space-y-2">
          {requests.map(r => (
            <div
              key={r.id}
              className="flex items-start justify-between gap-3 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  {r.call_groups && (
                    <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                      {r.call_groups.name}
                    </span>
                  )}
                  {r.call_type && (
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                      {r.call_type}
                    </span>
                  )}
                </div>
                {r.location && (
                  <p className="text-xs text-slate-500 truncate">{r.location}</p>
                )}
                {r.description && (
                  <p className="text-xs text-slate-400 truncate mt-0.5">{r.description}</p>
                )}
                <p className="text-[10px] text-slate-300 dark:text-slate-600 mt-1">
                  {formatDate(r.created_at)}
                </p>
              </div>

              <div className="shrink-0 flex flex-col items-end gap-1.5">
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${statusColors[r.status]}`}>
                  {statusLabel[r.status]}
                </span>
                {r.status === 'pending' && (
                  <button
                    onClick={() => updateStatus({ id: r.id, status: 'acknowledged' })}
                    className="text-[10px] font-black text-blue-600 hover:underline"
                  >
                    استلام
                  </button>
                )}
                {r.status === 'acknowledged' && (
                  <button
                    onClick={() => updateStatus({ id: r.id, status: 'resolved' })}
                    className="text-[10px] font-black text-green-600 hover:underline"
                  >
                    إغلاق
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   IncidentsTab (reuses same logic as SupervisorPage)
══════════════════════════════════════════════════════════════════ */

function IncidentsTab({ profile }: { profile: ProfileWithDept | null }) {
  const [selectedIncident, setSelectedIncident] = useState<IncidentRow | null>(null);
  const [actionType, setActionType]             = useState<'update' | 'close' | 'escalate' | 'direct' | 'request_rca'>('update');
  const [actionDesc, setActionDesc]             = useState('');
  const [escalateTo, setEscalateTo]             = useState<'medical_director' | 'executive_director'>('medical_director');
  const [actionError, setActionError]           = useState<string | null>(null);

  const { data: incidents = [], isLoading } = useIncidents({ dept_id: undefined });
  const { mutateAsync: doAction, isPending: actionPending } = useIncidentAction();

  const handleAction = async () => {
    if (!selectedIncident) return;
    setActionError(null);
    try {
      await doAction({
        incident_id: selectedIncident.id,
        action_type: actionType,
        description: actionDesc || undefined,
        escalate_to: actionType === 'escalate' ? escalateTo : undefined,
      });
      setSelectedIncident(null);
      setActionDesc('');
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'حدث خطأ غير متوقع');
    }
  };

  return (
    <>
      <div className="space-y-3">
        {isLoading ? (
          <div className="glass-panel rounded-2xl p-10 text-center">
            <span className="spinner border-blue-200 border-t-blue-600" />
          </div>
        ) : incidents.length === 0 ? (
          <div className="glass-panel rounded-2xl p-10 text-center">
            <p className="text-slate-400 font-bold text-sm">لا توجد أحداث</p>
          </div>
        ) : (
          incidents.map(inc => {
            const colors = RISK_LEVEL_COLORS[inc.risk_level] ?? RISK_LEVEL_COLORS['منخفض'];
            const sla    = formatSlaTime(inc.created_at, inc.sla_minutes, inc.status);
            return (
              <button key={inc.id} onClick={() => setSelectedIncident(inc)}
                className="glass-panel rounded-2xl p-4 w-full text-right hover:shadow-md transition-all">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-black text-slate-400">{inc.event_id}</span>
                      {sla?.breached && <span className="text-[10px] font-black bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">SLA</span>}
                    </div>
                    <p className="text-sm font-black text-slate-800 dark:text-white truncate">{inc.event_type}</p>
                    <p className="text-xs text-slate-500 truncate">{inc.location}</p>
                  </div>
                  <span className={`shrink-0 text-xs font-black px-2.5 py-1 rounded-full border ${colors.badge}`}>
                    {inc.risk_level}
                  </span>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Action Modal */}
      {selectedIncident && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="glass-panel w-full max-w-md rounded-2xl p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-black text-slate-800 dark:text-white">إجراء</h3>
                <p className="text-xs text-slate-500 mt-0.5">{selectedIncident.event_id}</p>
              </div>
              <button onClick={() => setSelectedIncident(null)} className="p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
                <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {(['update','close','escalate','direct','request_rca'] as const).map(a => (
                <button key={a} onClick={() => setActionType(a)}
                  className={`py-2 rounded-xl font-black text-xs border transition-all ${
                    actionType === a ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                  }`}>
                  {{ update:'تحديث', close:'إغلاق', escalate:'تصعيد', direct:'توجيه', request_rca:'RCA' }[a]}
                </button>
              ))}
            </div>

            {actionType === 'escalate' && (
              <div className="mb-4 space-y-2">
                {([
                  { value: 'medical_director',   label: 'المدير الطبي' },
                  { value: 'executive_director', label: 'المدير التنفيذي' },
                ] as const).map(opt => (
                  <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${
                    escalateTo === opt.value ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                  }`}>
                    <input type="radio" name="esc" value={opt.value} checked={escalateTo === opt.value}
                      onChange={() => setEscalateTo(opt.value)} className="w-4 h-4" />
                    <span className="text-sm font-bold text-slate-800 dark:text-white">{opt.label}</span>
                  </label>
                ))}
              </div>
            )}

            <textarea rows={3} className="input-field resize-none mb-4" placeholder="ملاحظات..."
              value={actionDesc} onChange={e => setActionDesc(e.target.value)} />

            {actionError && (
              <div className="mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-2 text-center">
                <p className="text-red-600 text-xs font-bold">{actionError}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setSelectedIncident(null)} className="btn-secondary flex-1">إلغاء</button>
              <button onClick={handleAction} disabled={actionPending}
                className="flex-1 py-2.5 rounded-xl font-black text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-all">
                {actionPending ? <span className="spinner border-white/30 border-t-white" /> : 'تنفيذ'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
